import { apiFetch } from "./auth";
import { triggerSaga } from "./saga";
import type { ListSagasResult, TriggerSagaResult } from "../types/saga";
import type { AuditLog } from "../types/audit";
import type {
  ListTenantsParams,
  ListTenantsResult,
  Tenant,
  TriggerOnboardingInput,
  TriggerOnboardingResult,
} from "../types/tenant";

interface ApiSuccess<T> { success: true; data: T }
interface ApiPaginated<T> { success: true; data: T[]; pagination: { limit: number; offset: number; total: number } }

export async function triggerOnboarding(input: TriggerOnboardingInput): Promise<TriggerOnboardingResult> {
  const idempotencyKey = input.idempotencyKey ?? `tenant:${input.tenantKey}`;
  const body = await apiFetch<ApiSuccess<TriggerOnboardingResult>>("/api/onboardings", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({
      tenantKey: input.tenantKey,
      name: input.name,
      brandingColor: input.brandingColor,
      billingTier: input.billingTier,
    }),
  });
  return body.data;
}

export async function listTenants(params: ListTenantsParams = {}): Promise<ListTenantsResult> {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.billingTier) search.set("billingTier", params.billingTier);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  const path = qs ? `/api/tenants?${qs}` : "/api/tenants";
  const body = await apiFetch<ApiPaginated<Tenant>>(path);
  return { data: body.data, pagination: body.pagination };
}

export async function getTenant(tenantKey: string): Promise<Tenant> {
  const body = await apiFetch<ApiSuccess<Tenant>>(`/api/tenants/${tenantKey}`);
  return body.data;
}

/**
 * Dispara saga ArchiveTenant via endpoint genérico de saga.
 * SPEC-20260503-0529.
 */
export async function archiveTenant(tenantKey: string): Promise<TriggerSagaResult> {
  return triggerSaga({
    sagaType: "ArchiveTenant",
    input: { tenantKey },
    idempotencyKey: `archive:${tenantKey}:${Date.now()}`,
  });
}

/**
 * Lista sagas relacionadas a um tenant (filtro JSONB containment no backend).
 * SPEC-20260503-1414.
 */
export async function getTenantSagas(tenantKey: string, params: { limit?: number; offset?: number } = {}): Promise<ListSagasResult> {
  const search = new URLSearchParams();
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  const path = qs ? `/api/tenants/${tenantKey}/sagas?${qs}` : `/api/tenants/${tenantKey}/sagas`;
  const body = await apiFetch<ApiPaginated<ListSagasResult["data"][number]>>(path);
  return { data: body.data, pagination: body.pagination };
}

/** Lista audit logs de um tenant. SPEC-20260503-1414. */
export async function getTenantAuditLogs(tenantKey: string): Promise<AuditLog[]> {
  const body = await apiFetch<ApiSuccess<AuditLog[]>>(`/api/tenants/${tenantKey}/audit-logs`);
  return body.data;
}

/** Dispara saga RestoreTenant. SPEC-20260503-0529. */
export async function restoreTenant(tenantKey: string): Promise<TriggerSagaResult> {
  return triggerSaga({
    sagaType: "RestoreTenant",
    input: { tenantKey },
    idempotencyKey: `restore:${tenantKey}:${Date.now()}`,
  });
}

/**
 * Dispara saga MigrateTenantTier para upgrade/downgrade de plano.
 * SPEC-20260503-0532.
 */
export async function migrateTenantTier(tenantKey: string, targetTier: string): Promise<TriggerSagaResult> {
  return triggerSaga({
    sagaType: "MigrateTenantTier",
    input: { tenantKey, targetTier },
    idempotencyKey: `migrate-tier:${tenantKey}:${targetTier}:${Date.now()}`,
  });
}

/**
 * Dispara saga RotateTenantSecrets para rotação de credenciais downstream.
 * SPEC-20260503-0531.
 */
export async function rotateTenantSecrets(tenantKey: string): Promise<TriggerSagaResult> {
  return triggerSaga({
    sagaType: "RotateTenantSecrets",
    input: { tenantKey },
    idempotencyKey: `rotate-secrets:${tenantKey}:${Date.now()}`,
  });
}

/**
 * Dispara saga DeleteTenantPermanently com confirmation header
 * `X-Confirm-Delete: yes`. SPEC-20260503-0530. Hard-delete irreversível.
 */
export async function deleteTenantPermanently(tenantKey: string): Promise<TriggerSagaResult> {
  const idempotencyKey = `delete-permanent:${tenantKey}:${Date.now()}`;
  const body = await apiFetch<ApiSuccess<TriggerSagaResult>>("/api/sagas", {
    method: "POST",
    headers: {
      "Idempotency-Key": idempotencyKey,
      "X-Confirm-Delete": "yes",
    },
    body: JSON.stringify({
      sagaType: "DeleteTenantPermanently",
      input: { tenantKey },
      idempotencyKey,
    }),
  });
  return body.data;
}
